import React, { FC, ReactNode, useMemo } from 'react';
import { ReactComponent as ErrorIcon } from '@blink/assets/src/icons/alertCircle.svg';
import { ReactComponent as SuccessIcon } from '@blink/assets/src/icons/checkCircleNotFilled.svg';
import { ReactComponent as AlertIcon } from '@blink/assets/src/icons/warningNotFilled.svg';
import { ReactComponent as HealthIcon } from '@blink/assets/src/icons/loaderDashWhite.svg';
import { ReactComponent as ExclamationIcon } from '@blink/assets/src/icons/exclamation.svg';

import './InfoCard.css';

import { cn } from '@/shared/lib/tailwind-merge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui';

type InfoCardProps = {
    type: IconTypes;
    color?: IconColors;
    text: ReactNode;
    classes?: Partial<Record<InfoCardClasses, string>>;
    tooltipText?: string | ReactNode;
    children?: ReactNode;
};

export enum IconTypes {
    ALERT = 'alert',
    ERROR = 'error',
    SUCCESS = 'success',
    INFO = 'info',
    LOADER = 'loader',
}

export enum IconColors {
    ALERT = 'alert',
    ERROR = 'error',
    SUCCESS = 'success',
    INFO = 'info',
}

type InfoCardClasses = 'root';

interface InfoCardComponent extends FC<InfoCardProps> {
    types: typeof IconTypes;
    colors: typeof IconColors;
}

const InfoCard: InfoCardComponent = ({ type, color, text, classes, tooltipText }) => {
    const colorStyles = useMemo(() => {
        switch (color) {
            case IconColors.SUCCESS:
                return 'bg-background-lightPositive text-content-positive border border-border-positive';
            case IconColors.ERROR:
                return 'bg-background-lightNegative text-content-negative border border-border-negative';
            case IconColors.ALERT:
                return 'bg-background-lightWarning text-content-warning border border-border-warning';
            case IconColors.INFO:
                return 'text-content-secondary border border-border-opaque';
            default:
                return '';
        }
    }, [color]);

    const icon = useMemo(() => {
        switch (type) {
            case IconTypes.SUCCESS:
                return <SuccessIcon className='w-6 h-6' />;
            case IconTypes.ERROR:
                return <ErrorIcon className='w-6 h-6' />;
            case IconTypes.ALERT:
                return <AlertIcon className='w-6 h-6' />;
            case IconTypes.INFO:
                return <ExclamationIcon className='w-6 h-6' />;
            case IconTypes.LOADER:
                return <HealthIcon className='w-6 h-6' />;
            default:
                return null;
        }
    }, [type]);

    return (
        <div
            className={cn(
                'flex items-center gap-3 p-4 rounded info-card',
                colorStyles,
                classes?.root,
            )}
        >
            {tooltipText ? (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>{icon}</TooltipTrigger>
                        <TooltipContent side='bottom' sideOffset={38}>
                            {tooltipText}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ) : (
                icon
            )}
            <div>{text}</div>
        </div>
    );
};

InfoCard.types = IconTypes;
InfoCard.colors = IconColors;

export { InfoCard };
